import { createServerFn } from "@tanstack/react-start";

/**
 * Gate for passwordless sign-in requests.
 *
 * - 5 magic-link requests per hour per email address, 10 per hour per caller IP.
 * - Only existing accounts qualify: an unknown email is refused here, and the
 *   client also passes `shouldCreateUser: false`, so a magic link can never
 *   create an account and can never bypass the early-access approval gate.
 * - The email address is never stored in the counter table (hashed key).
 */
export const guardMagicLink = createServerFn({ method: "POST" })
  .inputValidator((data: { email: string }) => ({
    email: String(data?.email ?? "")
      .trim()
      .slice(0, 320)
      .toLowerCase(),
  }))
  .handler(async ({ data }) => {
    const generic = {
      ok: true as const,
      message: "If that email has an account, a sign-in link is on its way.",
    };
    if (!data.email || !data.email.includes("@")) {
      return { ok: false as const, message: "Enter a valid email address." };
    }

    const { rateLimit, callerKey, hashedKey } = await import("./rate-limit.server");

    const byIp = await rateLimit("magic:ip", callerKey(), 10, 3600);
    if (!byIp.allowed) {
      const mins = Math.ceil(Math.max(byIp.retryAfter, 1) / 60);
      return {
        ok: false as const,
        message: `Too many sign-in link requests. Try again in about ${mins} minute${mins === 1 ? "" : "s"}.`,
      };
    }
    const byEmail = await rateLimit("magic:email", hashedKey(data.email), 5, 3600);
    if (!byEmail.allowed) {
      const mins = Math.ceil(Math.max(byEmail.retryAfter, 1) / 60);
      return {
        ok: false as const,
        message: `Too many sign-in link requests for that email. Try again in about ${mins} minute${mins === 1 ? "" : "s"}.`,
      };
    }

    // Same answer for every address. The client sends with
    // shouldCreateUser: false, so unknown emails simply receive nothing.
    return generic;
  });
